import dayjs from "dayjs"
import {
  createContext,
  useCallback,
  useContext,
  useState,
  useMemo,
} from "react"
import type {
  FC,
  ReactNode,
} from "react"

export type ReloadState = {
  requestReload: () => void;
  reloadRequestedAt: string; // string is a date string
}

interface Props {
  children?: ReactNode;
}

export type UseReloadingContext = () => ReloadState

/*
 * this creates a context that is consumed by simple loaders
 *
 * when the `requestReload` function is called, the timestamp for a
 * reloadRequest is updated. the simple loader responds to this change by
 * re-executing the loader.
 */
export function createReloadingContextProvider(): [
  FC<Props>,
  UseReloadingContext,
] {
  const ReloadingContext = createContext<ReloadState>({
    reloadRequestedAt: "",

    requestReload: () => {},
  })

  const useReloadingContext = () => useContext(ReloadingContext)

  const ReloadingContextProvider = (props: Props) => {
    const [
      reloadRequestedAt,
      setReloadRequest,
    ] = useState<ReloadState["reloadRequestedAt"]>("")

    // the requestReload value should never change because its only
    // dependencies are setState and a ref
    const requestReload = useCallback(
      () => {
        setReloadRequest(dayjs().toISOString())
      },
      [setReloadRequest],
    )

    const reloadingState = useMemo(
      () => ({
        reloadRequestedAt,
        requestReload,
      }),
      [
        reloadRequestedAt,
        requestReload,
      ],
    )

    return (
      <ReloadingContext.Provider value={reloadingState}>
        {props.children}
      </ReloadingContext.Provider>
    )
  }

  return [
    ReloadingContextProvider,
    useReloadingContext,
  ]
}
